import type { ReactElement, ReactNode } from 'react';
import { Container } from '@vwpfs/mary-air-lib/components/air/03-base/Container';
import { FlexRow } from '@vwpfs/mary-air-lib/components/air/03-base/Flex/Row';
import { FillUp } from '@vwpfs/mary-air-lib/components/air/04-helpers/FillUp';
import { Button } from '@vwpfs/mary-air-lib/components/air/05-atoms/Button';
import { IconNames } from '@vwpfs/mary-air-lib/components/air/05-atoms/Icon';
import { Modal } from '@vwpfs/mary-air-lib/components/air/06-molecules/Modal';
import {
  Navbar,
  NavbarIcon,
  NavbarStack,
  NavbarStackContent,
  NavbarStackRight,
  NavbarTitle
} from '@vwpfs/mary-air-lib/components/air/07-organisms';
import { ThemePalette, ThemePaletteState, ThemeSize } from '@vwpfs/mary-air-lib/theme/_Types';

interface Props {
  readonly id: string;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /**
   * Render the navbar ✕. Off by default, because a modal with a Cancel button
   * does not need a second control doing the same thing. Turn it on for a
   * read-only modal, where there is no Cancel and the ✕ is the only way out.
   */
  readonly showCloseIcon?: boolean;
}

/**
 * Modal shell for the portal.
 *
 * Mirrors mary-air-lib's ModalNotification chrome (warning navbar + scrollable
 * body), but deliberately omits its hardcoded footer: that footer renders a
 * "Close" button wired to the same handler as the caller's own "Cancel"
 * button, so the two read as different actions while doing the same thing.
 * Callers render their own action row instead.
 *
 * The navbar ✕ follows the same rule and is off by default. A modal that
 * submits something closes through its own Cancel; only a read-only modal,
 * which has no Cancel, opts in with `showCloseIcon`.
 */
export function VapModal({ id, title, onClose, children, showCloseIcon = false }: Props): ReactElement {
  return (
    <dialog open aria-label={title}>
      <Modal modalID={id} theme={{ size: ThemeSize.SMALL }}>
        <FlexRow isFixed>
          <Navbar theme={{ paletteState: ThemePaletteState.WARNING }}>
            <NavbarStack>
              <NavbarStackContent>
                <NavbarTitle>{title}</NavbarTitle>
              </NavbarStackContent>
              {showCloseIcon && (
                <NavbarStackRight>
                  <NavbarIcon>
                    <Button
                      className="scl-o-navbar__icon"
                      icon={IconNames.TIMES}
                      title="Close"
                      link={{ onClick: onClose }}
                    />
                  </NavbarIcon>
                </NavbarStackRight>
              )}
            </NavbarStack>
          </Navbar>
        </FlexRow>
        <FlexRow theme={{ palette: ThemePalette.CONTRAST_PRIMARY }} isScrollable>
          <FillUp>
            <Container theme={{ padding: { '': { y: 3 }, md: { y: 4 } } }}>{children}</Container>
          </FillUp>
        </FlexRow>
      </Modal>
    </dialog>
  );
}
